
import React from 'react';
import { BrowserRouter, Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { LandingLayout } from '@/components/layout/LandingLayout';
import { ThemeProvider } from 'next-themes';
import { AuthProvider } from '@/context/AuthContext';
import { SidebarProvider } from '@/context/SidebarContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import pages
import Index from '@/pages/Index';
import Profile from '@/pages/Profile';
import Places from '@/pages/places';
import BusinessDetails from '@/pages/BusinessDetails';
import Dashboard from '@/pages/Dashboard';
import Auth from '@/pages/Auth';
import UpdatePassword from '@/pages/UpdatePassword';
import NotFound from '@/pages/NotFound';
import Tasks from '@/pages/Tasks';
import Companies from '@/pages/Companies';
import CompanyDetails from '@/pages/CompanyDetails';
import NewCompany from '@/pages/NewCompany';
import EditCompany from '@/pages/EditCompany';
import Admin from '@/pages/Admin';
import Documentation from '@/pages/Documentation';
import Subscriptions from '@/pages/Subscriptions';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import SubscriptionReturn from '@/pages/SubscriptionReturn';
import Payment from '@/pages/Payment';
import LandingPage from '@/pages/LandingPage';
import Features from '@/pages/Features';
import PricingPage from '@/pages/Pricing';
import HowItWorks from '@/pages/HowItWorks';
import InviteRedirect from '@/pages/InviteRedirect';

// Define error handler for query client
const queryErrorHandler = (error: unknown) => {
  console.error('API error:', error);
};

// PERFORMANCE: Optimized query client configuration
// Different staleTime values for different data types:
// - Static data (companies, profiles): 15 minutes
// - Dynamic data (leads, activities): 5 minutes
// - Real-time data (notifications): 1 minute
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1, // Single retry for transient failures
      staleTime: 5 * 60 * 1000, // 5 minutes default
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache
      refetchOnReconnect: 'always',
      refetchOnMount: false, // Don't refetch if data is fresh
      throwOnError: false,
    },
    mutations: {
      throwOnError: false,
      retry: 0, // Don't retry mutations by default
    },
  }
});

// Import components for error boundary
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthErrorBoundary } from '@/components/AuthErrorBoundary';
import './App.css';

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light">
        <AuthProvider>
          <SidebarProvider>
            <AuthErrorBoundary>
              <BrowserRouter>
                <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/update-password" element={<UpdatePassword />} />
            <Route path="/invite/:token" element={<InviteRedirect />} />
              
              {/* Landing layout for unprotected routes */}
              <Route element={<LandingLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/subscription/return" element={<SubscriptionReturn />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              </Route>
              
              {/* Documentation - available to both logged in and logged out users */}
              <Route path="/documentation" element={
                <AppLayout>
                  <Documentation />
                </AppLayout>
              } />
              
              {/* Protected routes with app layout */}
              <Route element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }>
                <Route element={<AppLayout>
                  <Outlet />
                </AppLayout>}>
                  <Route path="/app" element={<Index />} />
                  <Route path="/places" element={<Places />} />
                  <Route path="/leads/:id" element={<BusinessDetails />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/companies" element={<Companies />} />
                  <Route path="/companies/new" element={<NewCompany />} />
                  <Route path="/companies/:id" element={<CompanyDetails />} />
                  <Route path="/companies/:id/edit" element={<EditCompany />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/subscription" element={<Subscriptions />} />
                  <Route path="/payment" element={<Payment />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
            </AuthErrorBoundary>
          </SidebarProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
